import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import moment from 'moment';
import Dropdown from 'react-bootstrap/lib/Dropdown';
import ButtonGroup from 'react-bootstrap/lib/ButtonGroup';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import i18n from '../../../src/i18n/translate';
import Button from '../Button';
import ErrorPage from '../ErrorPage';
import Icon from '../Icon';
import { toggleElementFullscreen } from '../../Util';
import isEqual from 'lodash/isEqual';
import GenericModal from '../GenericModal';
import ProgressSpinner from '../../material/ProgressSpinner';

export default class TableauChart extends Component {

  static propTypes = {
    chartSourceUrl: PropTypes.string.isRequired,
    chart: PropTypes.shape({
      show_toolbar: PropTypes.boolean,
      csv_view: PropTypes.string,
      csv_workbook: PropTypes.string,
      disable_refresh: PropTypes.boolean,
      export_availability: PropTypes.string
    }).isRequired,
    errorMessage: PropTypes.string,
    filters: PropTypes.object,
    onError: PropTypes.func.isRequired,
    showReportToolbar: PropTypes.bool,
    createDataExport: PropTypes.func,
    currentEntity: PropTypes.object,
    longDataExportPoll: PropTypes.func,
    padding: PropTypes.string,
    shortDataExportPoll: PropTypes.func,
    selectDataExport: PropTypes.func,
    tableau: PropTypes.shape({
      Viz: PropTypes.func.isRequired,
      FilterUpdateType: PropTypes.shape({
        REPLACE: PropTypes.any
      })
    }).isRequired,
    toolbarType: PropTypes.oneOf(['standard', 'light'])
  };

  static defaultProps = {
    filters: {},
    showReportToolbar: true
  };

  constructor(props) {
    super(props);
    this.state = {
      showExportModal: false
    };
  }

  componentDidMount() {
    const { filters } = this.props;
    const containerDiv = ReactDOM.findDOMNode(this.chartDiv);
    const validFilters = filters ? Object.fromEntries(Object.entries(filters).filter(([_, val]) => val !== null && val !== undefined)) : {};
    const options = {
      hideToolbar: !this.props.chart.show_toolbar,
      onFirstInteractive: () => {
        this.workbook = this.viz.getWorkbook();
        this.dash = this.viz.getWorkbook().getActiveSheet();
      },
      ...validFilters
    };

    try {
      this.viz = new this.props.tableau.Viz(containerDiv, this.props.chartSourceUrl, options);
    } catch (e) {
      this.viz = null;
    }
    if (!this.viz) {
      this.setState({ error: i18n.t('Unable to connect to tableau.') });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (this.dash && nextProps.filters && !isEqual(this.props.filters, nextProps.filters)) {
      // Apply all passed filters with valid values for each worksheet
      Object.keys(nextProps.filters)
        .filter(key => nextProps.filters[key] !== null && nextProps.filters[key] !== undefined)
        .forEach(key => {
          this.dash.getWorksheets().forEach(worksheet => {
            worksheet.applyFilterAsync(key, nextProps.filters[key], this.props.tableau.FilterUpdateType.REPLACE);
          });
        });
    }
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.currentEntity !== this.props.currentEntity) {
      if (['completed', 'error', 'completed_without_download'].includes(this.props.currentEntity.status)) {
        this.setState({ pollingExport: false }, this.clearIntervals());
      }
      if (prevProps.currentEntity && !prevProps.currentEntity.id && this.props.currentEntity.id && this.state.pollingExport) {
        this.initialPolling = await this.props.shortDataExportPoll(this.props.currentEntity.id);
        this.longPolling = await this.props.longDataExportPoll(this.props.currentEntity.id)
      }
      if (this.props.currentEntity.status === 'error' || this.props.errorMessage) {
        this.setState({ pollingExport: false, showExportModal: false });
      }
    }
  }

  componentWillUnmount() {
    this.clearIntervals();
  }

  exportImage = () => {
    this.viz.showExportImageDialog();
  }

  clearIntervals = () => {
    clearInterval(this.initialPolling);
    delete this.initialPolling;
    clearInterval(this.longPolling);
    delete this.longPolling;
  }

  exportCrossTab = () => {
    try {
      this.viz.showExportCrossTabDialog(this.props.chart.csv_view);
    } catch (e) {
      const message = e.get_message ? e.get_message() : e;
      this.props.onError(`Unable to download, please contact an admin (${message})`);
    }
  }

  exportPDF = () => {
    this.viz.showExportPDFDialog();
  }

  refreshData = () => {
    this.viz.refreshDataAsync();
  }

  revertAll = () => {
    this.workbook.revertAllAsync();
  }

  getFilters = async () => {
    return async (sheets) => {
      for (const sheet of sheets) {
        const sheetFilters = await sheet.getFiltersAsync();
        const mergedFilters = await sheetFilters.reduce((acc, filter) => {
          if (filter && filter.getFilterType() === 'categorical' && !filter.getIsAllSelected() && !['Measure Names', 'Date Range Filter'].includes(filter.getFieldName())) {
            if (filter.getAppliedValues()) {
              acc[filter.getFieldName()] = filter.getAppliedValues().map(val => val.formattedValue?.replaceAll(",", "\\,")).join(",");
            }
          }
          return acc;
        }, {});
        return mergedFilters;
      }
    }
  }

  getParameters = async () => {
    return async (workbook) => {
      const parameters = await workbook.getParametersAsync();
      const filterParams = await parameters.reduce((acc, param) => {
        if (param && param.getDataType() === 'date') {
          acc[param.getName().replace(/\s/g, '')] = moment(param.getCurrentValue().formattedValue).format("YYYY-MM-DD");
        }
        return acc;
      }, {});

      return filterParams;
    }
  }

  exportData = async () => {
    await this.props.selectDataExport('add');
    const { currentEntity, chart: { csv_view, csv_workbook } } = this.props;
    let sheets = this.viz.getWorkbook().getActiveSheet().getWorksheets();
    let formFilters = await this.getFilters(sheets);
    let formedFilters = await formFilters(sheets);
    let formParameters = await this.getParameters(this.workbook);
    let formedParameters = await formParameters(this.workbook);

    currentEntity.filters = {};
    currentEntity.filters.filters = Object.assign({}, formedFilters, formedParameters);
    currentEntity.filters.view_name = String(csv_view);
    currentEntity.filters.workbook_name = String(csv_workbook);
    currentEntity.data_type = "Datafi::Exports::Tableau";
    currentEntity.name = this.workbook.getName();
    this.setState({ showExportModal: true, pollingExport: true })
    this.props.createDataExport(currentEntity);
  }

  closeExportModal = () => {
    this.clearIntervals();
    this.setState({ showExportModal: false, pollingExport: false });
  }

  toggleFullscreen = () => {
    const elem = this.chartDiv.parentElement;
    toggleElementFullscreen(elem);
  }

  csvDownload = () => {
    const { export_availability } = this.props.chart;
    const { pollingExport } = this.state;
    if (export_availability === 'workbook') {
      return (
        <MenuItem
          data-action="download-csv"
          eventKey="workbook"
          icon="collection-text"
        >
          <Icon iconType="collection-text" />{i18n.t('Download CSV')}
        </MenuItem>
      );
    } else if (export_availability === 'tableau_api') {
      return (
        <MenuItem
          data-action="download-csv"
          eventKey="tableau_api"
          disabled={pollingExport}
        >
          <Icon iconType={pollingExport ? "" : "collection-text"} />
          {pollingExport ? i18n.t('Generating Report...') : i18n.t('Download CSV')}
        </MenuItem>
      );
    } else {
      return null;
    }
  }

  selectDownload = (downloadType) => {
    let downloadAction;
    switch (downloadType) {
    case 'workbook':
      downloadAction = this.exportCrossTab;
      break;
    case 'tableau_api':
      downloadAction = this.exportData;
      break;
    case 'image':
      downloadAction = this.exportImage;
      break;
    case 'pdf':
      downloadAction = this.exportPDF;
      break;
    }
    downloadAction();
  }

  render() {
    const {
      chart: { disable_refresh },
      currentEntity: { download_url } = {},
      padding,
      showReportToolbar,
      toolbarType
    } = this.props;
    const downloadAvailable = !!download_url;
    const downloadModalTitle = downloadAvailable ? i18n.t("Report Ready") : i18n.t("Generating your report");
    const style = padding ?
      { padding } :
      undefined;

    if (this.state.error) {
      return <ErrorPage errors={[this.state.error]} title={i18n.t('Something Went Wrong.')} />;
    }
    let toolbarButtonClass = 'btn-primary';
    if (toolbarType === 'light') {
      toolbarButtonClass = 'btn-link';
    }
    return (
      <div className="chart">
        {showReportToolbar && (
          <div className="chart-toolbar" style={style}>
            <Button onClick={this.revertAll} className="chart-toolbar-button" style="link" icon="close" label={i18n.t('Clear Filters')} />
            {!disable_refresh && <Button onClick={this.refreshData} className="chart-toolbar-button" style="link" icon="replay" label={i18n.t('Refresh Data')} />}
            <Button onClick={this.toggleFullscreen} className="chart-toolbar-button" style="link" icon="fullscreen-alt" label={i18n.t('Full Screen')} />
            <ButtonGroup className="chart-download-dropdown">
              <Dropdown
                data-action="download-report"
                id="chart-download-button"
                onSelect={this.selectDownload}
              >
                <Dropdown.Toggle bsRole="toggle" className={`chart-toolbar-button chart-download-button btn ${toolbarButtonClass}`}>
                  <Icon iconType="download" /> {i18n.t('Download Report')}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {this.csvDownload()}
                  <MenuItem
                    data-action="download-image"
                    eventKey="image"
                  >
                    <Icon iconType="collection-image" />{i18n.t('Download Image')}
                  </MenuItem>
                  <MenuItem
                    data-action="download-pdf"
                    eventKey="pdf"
                  >
                    <Icon iconType="collection-pdf" />{i18n.t('Download PDF')}
                  </MenuItem>
                </Dropdown.Menu>
              </Dropdown>
            </ButtonGroup>

          </div>)}
        <GenericModal size="medium" show={this.state.showExportModal} onClose={this.closeExportModal} title={downloadModalTitle} canClose={true} footerCloseButton={{ isTurnedOn: true, text: i18n.t("Close"), bsStyle: 'primary' }} >
          {!downloadAvailable &&
            <>
              <ProgressSpinner className={{ size: 'lg' }} />
              <div>
                <p>{i18n.t('This may take a little while. If you would like to wait for a link, please leave this modal open.')}</p>
                <p>{i18n.t('If you close the modal, we will email you when the file is ready.')}</p>
              </div>
            </>}
          {downloadAvailable && <div>{i18n.t('Your report is ready and available for download ')}<a download={true} href={download_url}>{i18n.t("here.")}</a></div>}
        </GenericModal>
        <div ref={div => { this.chartDiv = div }} style={style} />
      </div>
    );
  }
}
